import { useCallback, useEffect, useRef, useState } from "react";

import {
  GestionAusenciaActiva,
  getGestionActiva,
} from "src/services/gestionAusencia";

// Evento que dispara la pagina GestionAusencia tras responder exitosamente,
// para que el guard libere de inmediato sin esperar al proximo polling.
export const GESTION_AUSENCIA_REFETCH_EVENT = "gestion-ausencia-refetch";

const POLL_INTERVAL_MS = 30_000;

interface UseGestionAusenciaPendienteOptions {
  isAuth: boolean;
}

interface UseGestionAusenciaPendienteResult {
  gestionPendiente: GestionAusenciaActiva | null;
  refetch: () => void;
}

// Mantiene en memoria si el operario tiene una gestion de ausencia que requiere
// su respuesta (estado PENDIENTE o NOTIFICADO en backend). El backend ya filtra
// por estados respondibles; aqui solo trasladamos null = libre, no-null = bloquear.
//
// Politica ante fallos de red: NO bloquear (mejor permitir uso que dejar la app
// inutilizable por un blip de conectividad).
export function useGestionAusenciaPendiente(
  options: UseGestionAusenciaPendienteOptions
): UseGestionAusenciaPendienteResult {
  const { isAuth } = options;
  const [gestionPendiente, setGestionPendiente] = useState<GestionAusenciaActiva | null>(null);
  const isAuthRef = useRef(isAuth);
  isAuthRef.current = isAuth;

  const fetchActiva = useCallback(async () => {
    if (!isAuthRef.current) return;
    try {
      const activa = await getGestionActiva();
      if (!isAuthRef.current) return;
      setGestionPendiente(activa);
    } catch {
      if (!isAuthRef.current) return;
      setGestionPendiente(null);
    }
  }, []);

  // Carga inicial al autenticarse + limpieza al deslogearse
  useEffect(() => {
    if (!isAuth) {
      setGestionPendiente(null);
      return;
    }
    fetchActiva();
  }, [isAuth, fetchActiva]);

  // Polling mientras la sesion este activa
  useEffect(() => {
    if (!isAuth) return;
    const id = window.setInterval(() => {
      fetchActiva();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [isAuth, fetchActiva]);

  // Refetch al volver al foreground (usuario vuelve a la pestana / abre la PWA)
  useEffect(() => {
    if (!isAuth) return;
    const handler = () => {
      if (document.visibilityState === "visible") fetchActiva();
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, [isAuth, fetchActiva]);

  // Refetch manual disparado por GestionAusencia.tsx tras responder.
  // Optimistic clear primero para evitar que el guard rebote la siguiente
  // navegacion (history.replace que viene en el mismo tick) antes de que
  // el backend devuelva null. fetchActiva() confirma el estado real despues.
  useEffect(() => {
    const handler = () => {
      setGestionPendiente(null);
      fetchActiva();
    };
    window.addEventListener(GESTION_AUSENCIA_REFETCH_EVENT, handler);
    return () => window.removeEventListener(GESTION_AUSENCIA_REFETCH_EVENT, handler);
  }, [fetchActiva]);

  return { gestionPendiente, refetch: fetchActiva };
}
